// src/components/cards/WalletActivityCard.tsx
import React from 'react';
import { AggregatedWalletData, BlockchainNetwork } from '../../types';
import DataStatusIndicator, { DataStatus } from '../DataStatusIndicator';

interface WalletActivityCardProps {
  data: AggregatedWalletData | null;
  loading: boolean;
  error: string | null;
  lastUpdated: number;
  formatTimestamp: (timestamp: number) => string;
  significantMovementThreshold: number;
  dataStatus?: DataStatus;
}

const WalletActivityCard: React.FC<WalletActivityCardProps> = ({
  data,
  loading,
  error,
  lastUpdated,
  formatTimestamp,
  significantMovementThreshold,
  dataStatus = 'live'
}) => {
  // Format currency values
  const formatCurrency = (value: number): string => {
    if (value >= 1_000_000_000) {
      return `$${(value / 1_000_000_000).toFixed(2)}B`;
    } else if (value >= 1_000_000) {
      return `$${(value / 1_000_000).toFixed(2)}M`;
    } else {
      return `$${value.toLocaleString()}`;
    }
  };

  // Get color for network badge
  const getNetworkColor = (network: BlockchainNetwork): string => {
    switch (network) {
      case 'Ethereum':
        return 'wallet-network-ethereum';
      case 'Base':
        return 'wallet-network-base';
      case 'Solana':
        return 'wallet-network-solana';
      default:
        return '';
    }
  };

  // Get explorer URL for address
  const getExplorerUrl = (address: string, network: BlockchainNetwork): string => {
    switch (network) {
      case 'Ethereum':
        return `https://etherscan.io/address/${address}`;
      case 'Base':
        return `https://basescan.org/address/${address}`;
      case 'Solana':
        return `https://solscan.io/account/${address}`;
      default:
        return '#';
    }
  };

  // Format percentage with sign
  const formatPercentage = (value: number): string => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  return (
    <div className="bg-dashboard-card-light dark:bg-dashboard-card-dark rounded-card shadow-card p-5 dashboard-card relative">
      <DataStatusIndicator status={dataStatus} lastUpdated={lastUpdated} />
      
      <div className="flex justify-between items-start mb-4">
        <h3 className="text-lg font-semibold">Wallet Activity</h3>
        {loading && (
          <div className="animate-pulse px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded text-xs">
            Updating...
          </div>
        )}
      </div>

      {error ? (
        <div className="text-red-500 py-8 text-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p>{error}</p>
        </div>
      ) : data ? (
        <>
          {/* Summary Stats */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded">
              <div className="text-xs text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark mb-1">
                Tracked Wallets
              </div>
              <div className="text-lg font-medium">
                {data.totalWallets.toLocaleString()}
              </div>
            </div>
            <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded">
              <div className="text-xs text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark mb-1">
                Total SPX6900 Value
              </div>
              <div className="text-lg font-medium">
                {formatCurrency(data.totalBalanceUSD)}
              </div>
              <div className="text-xs text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark">
                (SPX6900 holdings only)
              </div>
            </div>
          </div>

          {/* Selling vs Buying Activity */}
          <div className="mb-4">
            <div className="text-sm font-medium mb-2">Wallet Activity</div>
            <div className="flex space-x-2">
              <div className="flex-1 bg-bull/10 rounded-lg p-2 text-center">
                <div className="text-xs text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark mb-1">
                  Buying
                </div>
                <div className="text-lg font-medium text-bull">
                  {data.buyingWallets}
                </div>
              </div>
              <div className="flex-1 bg-bear/10 rounded-lg p-2 text-center">
                <div className="text-xs text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark mb-1">
                  Selling
                </div>
                <div className="text-lg font-medium text-bear">
                  {data.sellingWallets}
                </div>
              </div>
            </div>
          </div>

          {/* SPX6900 Holdings Overview */}
          <div className="mb-4">
            <div className="text-sm font-medium mb-2">SPX6900 Holdings</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded">
                <div className="text-xs text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark mb-1">
                  Total Holdings
                </div>
                <div className="text-lg font-medium">
                  {formatCurrency(data.spx6900Metrics.totalSpx6900HoldingsUSD)}
                </div>
                <div className="text-xs text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark">
                  {(data.spx6900Metrics.totalSpx6900Holdings / 1000000).toFixed(2)}M tokens
                </div>
              </div>
              <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded">
                <div className="text-xs text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark mb-1">
                  Avg P&L
                </div>
                <div className={`text-lg font-medium ${
                  data.spx6900Metrics.averageSpx6900UnrealizedPnL >= 0 ? 'text-bull' : 'text-bear'
                }`}>
                  {formatPercentage(data.spx6900Metrics.averageSpx6900UnrealizedPnL)}
                </div>
              </div>
            </div>
          </div>

          {/* Whale Selling Alert */}
          {data.spx6900Metrics.whaleSellingAlert && (
            <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div>
                  <div className="text-sm font-medium text-red-800 dark:text-red-200">Whale Selling Alert</div>
                  <div className="text-xs text-red-600 dark:text-red-300">
                    Multiple whales are currently selling SPX6900
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 30-Day Selling Activity */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <div className="text-sm font-medium">30-Day Selling Activity</div>
              <div className="text-sm text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark">
                {data.spx6900Metrics.walletsCurrentlySelling} wallets selling
              </div>
            </div>
            <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark">
                  Tokens Sold (30d)
                </span>
                <span className="text-sm font-medium">
                  {(data.spx6900Metrics.totalSpx6900Sold30d / 1000000).toFixed(2)}M
                </span>
              </div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark">
                  USD Value
                </span>
                <span className="text-sm font-medium">
                  {formatCurrency(data.spx6900Metrics.totalSpx6900SoldUSD30d)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark">
                  Avg % Sold
                </span>
                <span className={`text-sm font-medium ${
                  data.spx6900Metrics.averagePercentageSold > 15 ? 'text-red-500' :
                  data.spx6900Metrics.averagePercentageSold > 5 ? 'text-orange-500' :
                  'text-green-500'
                }`}>
                  {data.spx6900Metrics.averagePercentageSold.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>

          {/* Sell Pressure Distribution */}
          <div className="mb-4">
            <div className="text-sm font-medium mb-2">Sell Pressure Distribution</div>
            <div className="space-y-2">
              {Object.entries(data.spx6900Metrics.sellPressureDistribution).map(([pressure, count]) => {
                const percentage = data.totalWallets > 0 ? (count / data.totalWallets) * 100 : 0;
                let colorClass = 'bg-gray-400';
                
                if (pressure === 'low') colorClass = 'bg-yellow-400';
                else if (pressure === 'medium') colorClass = 'bg-orange-400';
                else if (pressure === 'high') colorClass = 'bg-red-500';
                else if (pressure === 'none') colorClass = 'bg-green-400';
                
                return (
                  <div key={pressure} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className={`w-3 h-3 rounded-full ${colorClass} mr-2`}></div>
                      <span className="text-sm capitalize">{pressure}</span>
                    </div>
                    <div className="text-sm">
                      {count} ({percentage.toFixed(1)}%)
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Network Distribution */}
          <div className="mb-4">
            <div className="text-sm font-medium mb-2">Network Distribution</div>
            <div className="space-y-2">
              {Object.entries(data.networkBreakdown).map(([network, count]) => (
                count > 0 && (
                  <div key={network} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className={`${getNetworkColor(network as BlockchainNetwork)} wallet-network mr-2`}>
                        {network}
                      </span>
                    </div>
                    <div className="text-sm font-medium">
                      {count} ({((count / data.totalWallets) * 100).toFixed(1)}%)
                    </div>
                  </div>
                )
              ))}
            </div>
          </div>

          {/* Top Sellers */}
          {data.spx6900Metrics.topSellers && data.spx6900Metrics.topSellers.length > 0 && (
            <div className="mb-2">
              <div className="text-sm font-medium mb-2">Top SPX6900 Sellers</div>
              <div className="space-y-2">
                {data.spx6900Metrics.topSellers.slice(0, 3).map((seller) => (
                  <div key={seller.address} className="bg-gray-100 dark:bg-gray-800 p-3 rounded">
                    <div className="flex items-center justify-between mb-1">
                      <div className={`${getNetworkColor(seller.network)} wallet-network text-xs`}>
                        {seller.network}
                      </div>
                      <div className={`text-xs font-medium ${
                        seller.sellPressure === 'high' ? 'text-red-500' :
                        seller.sellPressure === 'medium' ? 'text-orange-500' :
                        seller.sellPressure === 'low' ? 'text-yellow-500' : 'text-gray-500'
                      }`}>
                        {seller.sellPressure.toUpperCase()} SELL
                      </div>
                    </div>
                    <a 
                      href={getExplorerUrl(seller.address, seller.network)} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline truncate mb-1 block"
                    >
                      {seller.address}
                    </a>
                    <div className="flex justify-between mb-1">
                      <span className="text-xs text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark">
                        30d Sold: {(seller.soldAmount / 1000000).toFixed(2)}M tokens
                      </span>
                      <span className="text-sm font-medium text-bear">
                        {formatCurrency(seller.soldAmountUSD)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark">
                        % of Holdings Sold
                      </span>
                      <span className={`text-sm font-bold ${
                        seller.percentageOfHoldingsSold > 30 ? 'text-red-600' :
                        seller.percentageOfHoldingsSold > 15 ? 'text-red-500' :
                        seller.percentageOfHoldingsSold > 5 ? 'text-orange-500' :
                        'text-yellow-500'
                      }`}>
                        {seller.percentageOfHoldingsSold.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top Wallets */}
          {data.topWallets && data.topWallets.length > 0 && (
            <div className="mb-2">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium">Tracked SPX6900 Wallets</div>
                <div className="text-xs text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark">
                  {data.topWallets.length} wallets ({data.topWallets.filter(w => w.spx6900Holdings.currentBalance !== null && w.spx6900Holdings.currentBalance > 0).length} holding)
                </div>
              </div>
              <div className="max-h-80 overflow-y-auto pr-2" style={{ scrollbarWidth: 'thin' }}>
                <div className="space-y-3">
                  {data.topWallets
                    .sort((a, b) => {
                      // Sort by balance, handling null values (put errors at end)
                      const aBalance = a.spx6900Holdings.currentBalanceUSD;
                      const bBalance = b.spx6900Holdings.currentBalanceUSD;
                      if (aBalance === null && bBalance === null) return 0;
                      if (aBalance === null) return 1;
                      if (bBalance === null) return -1;
                      return bBalance - aBalance;
                    })
                    .map((wallet, index) => (
                    <div key={wallet.address} className={`wallet-item p-3 rounded ${
                      wallet.spx6900Holdings.currentBalance !== null && wallet.spx6900Holdings.currentBalance > 0 
                        ? 'bg-gray-100 dark:bg-gray-800' 
                        : wallet.spx6900Holdings.currentBalance === null
                        ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                        : 'bg-gray-50 dark:bg-gray-900/50 opacity-75'
                    }`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark">
                            #{index + 1}
                          </span>
                          <div className={`${getNetworkColor(wallet.network)} wallet-network`}>
                            {wallet.network}
                          </div>
                        </div>
                        <div className={`text-xs font-medium ${
                          wallet.spx6900Holdings.currentBalance === null ? 'text-red-500' :
                          wallet.spx6900Holdings.currentBalance === 0 ? 'text-gray-500' :
                          wallet.spx6900Holdings.isCurrentlySelling ? 'text-bear' : 'text-bull'
                        }`}>
                          {wallet.spx6900Holdings.currentBalance === null ? 'Error' :
                           wallet.spx6900Holdings.currentBalance === 0 ? 'No Holdings' :
                           wallet.spx6900Holdings.isCurrentlySelling ? 'Selling' : 'Holding'}
                        </div>
                      </div>
                      <a 
                        href={getExplorerUrl(wallet.address, wallet.network)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="wallet-address text-xs text-blue-600 dark:text-blue-400 hover:underline truncate mt-1 block"
                      >
                        {wallet.address}
                      </a>
                      <div className="flex justify-between mt-1">
                        <span className={`text-xs ${
                          wallet.spx6900Holdings.currentBalance === null 
                            ? 'text-red-500 font-medium' 
                            : 'text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark'
                        }`}>
                          {wallet.spx6900Holdings.currentBalance === null 
                            ? 'Error - API Failed'
                            : wallet.spx6900Holdings.currentBalance >= 1000000 
                            ? `${(wallet.spx6900Holdings.currentBalance / 1000000).toFixed(2)}M SPX6900`
                            : wallet.spx6900Holdings.currentBalance >= 1000
                            ? `${(wallet.spx6900Holdings.currentBalance / 1000).toFixed(1)}K SPX6900`
                            : `${wallet.spx6900Holdings.currentBalance.toFixed(0)} SPX6900`
                          }
                        </span>
                        <span className={`text-sm font-medium ${
                          wallet.spx6900Holdings.currentBalance === null ? 'text-red-500' :
                          wallet.spx6900Holdings.unrealizedPnLPercentage >= 0 ? 'text-bull' : 'text-bear'
                        }`}>
                          {wallet.spx6900Holdings.currentBalance === null 
                            ? 'Error' 
                            : formatPercentage(wallet.spx6900Holdings.unrealizedPnLPercentage)
                          }
                        </span>
                      </div>
                      <div className={`text-xs mt-1 ${
                        wallet.spx6900Holdings.currentBalance === null 
                          ? 'text-red-500 font-medium' 
                          : 'text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark'
                      }`}>
                        Value: {wallet.spx6900Holdings.currentBalance === null 
                          ? 'Error - API Failed' 
                          : formatCurrency(wallet.spx6900Holdings.currentBalanceUSD || 0)
                        }
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Last Updated */}
          <div className="mt-2 text-xs text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark">
            Last updated: {formatTimestamp(lastUpdated || data.timestamp)}
          </div>
        </>
      ) : (
        <div className="py-8 text-center text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p>No wallet activity data available</p>
        </div>
      )}
    </div>
  );
};

export default WalletActivityCard;
